import React from "react";
import * as uuid from "uuid";
import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Button,
} from "@material-tailwind/react";
import cx from "classnames";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { inject } from "@vercel/analytics";
import TripPlan from "./components/TripPlan";
import "./App.css";
import { GoPlus } from "react-icons/go";
import { BiCoffeeTogo } from "react-icons/bi";
import { getRecipe } from "./helpers";

const ADD_TRIP_TEXT = "Add New Trip";
const LOCAL_STORAGE_KEY = "MISTY_TRIPS_RECORD";
const DEFAULT_STATE = [
  {
    id: "default",
    header: "Day 1-3",
    remark: "",
    recipes: ["2692000", "2690045", "2690044"],
    recipeCount: [1, 1, 1],
    toggleIngredientsView: false,
  },
];
const NEW_TRIP_HEADER = "New Trip";

const getInitialState = () => {
  const stateString = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stateString === null) {
    return DEFAULT_STATE;
  }
  const records = JSON.parse(stateString);
  if (records.length <= 0) {
    return DEFAULT_STATE;
  }
  return records;
};

function App() {
  const [trips, setTrips] = React.useState(getInitialState());
  const [focusTrip, setFocusTrip] = React.useState(
    trips.length > 0 ? trips[0] : {}
  );

  const updateTrip = React.useCallback((data) => {
    const { id } = data;
    setTrips((prev) => {
      const newState = [...prev];
      const index = prev.indexOf(prev.find((s) => s.id === id));
      newState[index] = data;
      return newState;
    });
  }, []);

  const removeTrip = React.useCallback((id) => {
    setTrips((prev) => {
      const newState = [...prev];
      const index = prev.indexOf(prev.find((s) => s.id === id));
      newState.splice(index, 1);
      setFocusTrip(newState[Math.max(0, index - 1)]);
      return newState;
    });
  }, []);

  React.useEffect(() => {
    const element = document.querySelector(
      `li[role="tab"][data-value="${focusTrip.id}"]`
    );
    if (element) {
      element.click();
    }
  }, [focusTrip]);

  React.useEffect(() => {
    document.querySelectorAll(`div[role="tabpanel"]`).forEach((element) => {
      const id = element.getAttribute("data-value");
      element.style.diplay = id === focusTrip.id ? "block" : "none !important";
    });
  }, [focusTrip]);

  React.useEffect(() => {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(trips));
  }, [trips]);

  React.useEffect(() => {
    inject();
  }, []);

  return (
    <div className="App py-6 md:h-full md:flex md:flex-col md:justify-start md:py-28">
      <div className="Container mx-3 flex flex-col justify-center md:mx-12">
        <Tabs value={focusTrip.id} style={{ overflow: "unset" }}>
          <DragDropContext
            onDragEnd={(result) => {
              if (result.destination) {
                const newTrips = Array.from(trips);
                const [removed] = newTrips.splice(result.source.index, 1);
                newTrips.splice(result.destination.index, 0, removed);
                setTrips(newTrips);
              }
            }}
          >
            <Droppable droppableId="tabs-header" direction="horizontal">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  <TabsHeader className="items-center flex-wrap rounded-b-none px-2">
                    {trips.map((trip, index) => (
                      <Draggable
                        key={trip.id}
                        draggableId={trip.id}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            className="mr-1 last:mr-0"
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Tab
                              className={cx(
                                "Tab uppercase font-bold text-sm w-fit px-4"
                              )}
                              key={trip.id}
                              value={trip.id}
                              onClick={() => setFocusTrip(trip)}
                            >
                              {trip.header || NEW_TRIP_HEADER}
                            </Tab>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    <button
                      className="bg-white drop-shadow-sm w-8 h-8 ml-auto text-center rounded-md hover:bg-gray-200 transition duration-150"
                      onClick={() => {
                        setTrips((prev) => [
                          ...prev,
                          {
                            id: uuid.v4(),
                            header: NEW_TRIP_HEADER,
                            remark: "",
                            recipes: [],
                            recipeCount: [],
                            toggleIngredientsView: false,
                          },
                        ]);
                      }}
                    >
                      <GoPlus className="m-auto" title={ADD_TRIP_TEXT} />
                    </button>
                  </TabsHeader>
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <TabsBody style={{ overflow: "unset" }}>
            {trips.map((trip) => (
              <TabPanel
                className={cx("p-0", {
                  "TabPanel--hidden": trip.id !== focusTrip.id,
                })}
                key={trip.id}
                value={trip.id}
              >
                <TripPlan
                  id={trip.id}
                  name={trip.header}
                  initialRemark={trip.remark}
                  initialRecipes={trip.recipes.map(getRecipe)}
                  initalCountMap={trip.recipeCount.reduce(
                    (prev, curr, index) => {
                      return Object.assign(prev, {
                        [trip.recipes[index]]: curr,
                      });
                    },
                    {}
                  )}
                  toggleIngredientsView={trip.toggleIngredientsView}
                  onChange={updateTrip}
                  onRemove={removeTrip}
                  disableRemove={trips.length <= 1}
                />
              </TabPanel>
            ))}
          </TabsBody>
        </Tabs>

        <a
          className="sm:ml-auto mt-2 sm:w-fit w-full opacity-75 hover:opacity-100"
          target="_blank"
          rel="noreferrer"
          href="https://www.buymeacoffee.com/tesvan"
        >
          <Button
            color="orange"
            className="flex flex-col w-full sm:w-fit md:flex-row justify-center items-center"
          >
            <BiCoffeeTogo className="md:mr-2 h-4 w-4" />
            Buy me a coffee
          </Button>
        </a>
      </div>
    </div>
  );
}

export default App;
